'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

const supabase = createClient();

interface ReportCondiviso {
  id: string;
  token: string;
  titolo: string;
  tipo: string;
  scadenza: string | null;
  accessi: number;
  attivo: boolean;
  logo_url: string | null;
  created_at: string;
}

const TIPI_REPORT = ['kpi', 'ticket', 'inventario', 'custom'];

export default function ReportCondivisiContent() {
  const [reports, setReports] = useState<ReportCondiviso[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ titolo: '', tipo: 'kpi', scadenza: '', logo_url: '' });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const { profile } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('report_condivisi').select('*').order('created_at', { ascending: false });
    setReports(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createReport = async () => {
    if (!form.titolo.trim()) return;
    setSaving(true);

    // Fetch live data snapshot based on type
    let snapshot: Record<string, any> = {};
    if (form.tipo === 'kpi') {
      const { data } = await supabase.from('kpi_snapshot').select('*').order('data_snapshot', { ascending: false }).limit(1);
      snapshot = data?.[0] || {};
    } else if (form.tipo === 'ticket') {
      const { data } = await supabase.from('tickets').select('stato, priorita, sla_stato').limit(100);
      snapshot = { tickets: data || [] };
    } else if (form.tipo === 'inventario') {
      const { data } = await supabase.from('devices').select('tipo, stato').limit(200);
      snapshot = { devices: data || [] };
    }

    await supabase.from('report_condivisi').insert({
      titolo: form.titolo,
      tipo: form.tipo,
      scadenza: form.scadenza || null,
      logo_url: form.logo_url || null,
      dati_snapshot: snapshot,
      creato_da: profile?.id,
    });
    setShowForm(false);
    setForm({ titolo: '', tipo: 'kpi', scadenza: '', logo_url: '' });
    setSaving(false);
    fetchData();
  };

  const copyLink = (token: string) => {
    const url = `${window.location.origin}/report-pubblico/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  const toggleReport = async (id: string, attivo: boolean) => {
    await supabase.from('report_condivisi').update({ attivo: !attivo }).eq('id', id);
    setReports(prev => prev.map(r => r.id === id ? { ...r, attivo: !attivo } : r));
  };

  const deleteReport = async (id: string) => {
    await supabase.from('report_condivisi').delete().eq('id', id);
    setReports(prev => prev.filter(r => r.id !== id));
  };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Report Condivisi' }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Condivisione Report con Link Pubblico</h1>
          <p className="text-text-secondary text-sm mt-1">Genera link pubblici temporanei per condividere report con clienti e direzione</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
          <Icon name="ShareIcon" size={16} /> Nuovo Report
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 bg-surface-elevated border border-border rounded-xl">
          <Icon name="ShareIcon" size={48} className="mx-auto mb-4 text-muted-color opacity-50" />
          <h3 className="text-white font-semibold mb-2">Nessun report condiviso</h3>
          <p className="text-muted-color text-sm mb-4">Crea il primo report con link pubblico</p>
          <button onClick={() => setShowForm(true)} className="bg-sky-600 hover:bg-sky-500 text-white px-6 py-2 rounded-lg text-sm font-semibold">
            Crea Report
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(report => (
            <div key={report.id} className="bg-surface-elevated border border-border rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-white">{report.titolo}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${report.attivo ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}>
                      {report.attivo ? 'Attivo' : 'Disattivato'}
                    </span>
                    <span className="text-xs bg-sky-500/10 text-sky-400 px-2 py-0.5 rounded-full capitalize">{report.tipo}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-color">
                    <span className="flex items-center gap-1"><Icon name="EyeIcon" size={12} /> {report.accessi} accessi</span>
                    <span className="flex items-center gap-1"><Icon name="CalendarIcon" size={12} /> {new Date(report.created_at).toLocaleDateString('it-IT')}</span>
                    {report.scadenza && <span className="flex items-center gap-1 text-amber-400"><Icon name="ClockIcon" size={12} /> Scade: {new Date(report.scadenza).toLocaleDateString('it-IT')}</span>}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="text-xs text-muted-color bg-surface rounded px-2 py-1 truncate max-w-xs">
                      {`${typeof window !== 'undefined' ? window.location.origin : ''}/report-pubblico/${report.token}`}
                    </code>
                    <button onClick={() => copyLink(report.token)}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${copied === report.token ? 'bg-green-500/10 text-green-400' : 'bg-surface hover:bg-surface-elevated text-muted-color hover:text-white'}`}>
                      <Icon name={copied === report.token ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={12} />
                      {copied === report.token ? 'Copiato!' : 'Copia Link'}
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleReport(report.id, report.attivo)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${report.attivo ? 'bg-green-500' : 'bg-gray-600'}`}>
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${report.attivo ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <button onClick={() => deleteReport(report.id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                    <Icon name="TrashIcon" size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-bold text-white">Nuovo Report Condiviso</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors">
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-muted-color mb-1">Titolo Report *</label>
                <input value={form.titolo} onChange={e => setForm(p => ({ ...p, titolo: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="Es. Report KPI Mensile - Gennaio 2026" />
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Tipo Report</label>
                <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                  {TIPI_REPORT.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Scadenza Link (opzionale)</label>
                <input type="datetime-local" value={form.scadenza} onChange={e => setForm(p => ({ ...p, scadenza: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">URL Logo Struttura (opzionale)</label>
                <input value={form.logo_url} onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white"
                  placeholder="https://..." />
              </div>
              <div className="bg-sky-500/5 border border-sky-500/20 rounded-lg p-3 text-xs text-sky-400">
                <Icon name="InformationCircleIcon" size={12} className="inline mr-1" />
                Il report includerà uno snapshot dei dati attuali. Il link sarà accessibile senza login.
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-border">
              <button onClick={() => setShowForm(false)} className="flex-1 bg-surface-elevated border border-border text-white px-4 py-2 rounded-lg text-sm font-semibold">Annulla</button>
              <button onClick={createReport} disabled={saving || !form.titolo.trim()}
                className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                {saving ? 'Creazione...' : 'Crea e Genera Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
